import { useEffect, useRef, useState } from "react";

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

export function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || inView) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25, ...options }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

export function useCountUp(target, active, duration = 1400) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    let frame;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.round(easeOutCubic(progress) * target));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, target, duration]);

  return value;
}

export const MARKETING_STATS = [
  { value: 4000, suffix: "+", label: "Students taught", hint: "School, Centre and online" },
  { value: 8, suffix: "+", label: "Schools taught in" },
  { value: 12, suffix: "+", label: "Years of teaching", hint: "IGCSE O-Level & A-Level · Edexcel" },
  { value: 2, suffix: "", label: "Programmes", hint: "O-Level & A-Level", display: "OL & AL" },
];

export const OFFERINGS = [
  {
    title: "Course full coverage",
    detail: "Every syllabus topic taught clearly from start to finish.",
    icon: "coverage",
  },
  {
    title: "Classified past papers",
    detail: "Topic-sorted past paper practice for targeted revision.",
    icon: "classified",
  },
  {
    title: "Past papers solving",
    detail: "Guided walkthroughs of real exam questions and mark schemes.",
    icon: "papers",
  },
  {
    title: "Weekly exams & mocks",
    detail: "Regular timed assessments to build exam stamina and accuracy.",
    icon: "exams",
  },
  {
    title: "24/7 assistance",
    detail: "Support when students need help — day or night.",
    icon: "assist",
  },
  {
    title: "Weekly parents checkup",
    detail: "Keep families informed on progress, effort, and next steps.",
    icon: "parents",
  },
  {
    title: "Live online updates",
    detail: "Track attendance, exams, and homework in one place.",
    icon: "updates",
  },
  {
    title: "Full videos & materials",
    detail: "Complete lesson videos and resources available online.",
    icon: "videos",
  },
];

const OFFER_ICONS = {
  coverage: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  classified: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
  papers: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M8 13h8M8 17h6" />
    </svg>
  ),
  exams: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  assist: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  ),
  parents: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  updates: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M23 4v6h-6M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  videos: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M10 9l6 3-6 3V9z" fill="currentColor" stroke="none" />
    </svg>
  ),
};

export function StatCard({ value, suffix = "+", label, hint, display, active, delay = 0 }) {
  const shouldCount = !display && typeof value === "number";
  const counted = useCountUp(shouldCount ? value : 0, active && shouldCount);
  const shown = display || `${counted}${suffix}`;

  return (
    <article
      className={`xm-stat-card ${active ? "is-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <strong className="xm-stat-value">{active || display ? shown : `0${suffix}`}</strong>
      <span className="xm-stat-label">{label}</span>
      {hint ? <span className="xm-stat-hint">{hint}</span> : null}
    </article>
  );
}

export function DemographicsSection({ title = "By the numbers", kicker = "Impact" }) {
  const [ref, inView] = useInView();

  return (
    <section className="xm-section xm-demo-section" ref={ref}>
      <div className="xm-wrap">
        <p className="xm-kicker">{kicker}</p>
        <h2 className="xm-section-title xm-display">{title}</h2>
        <p className="xm-section-copy xm-demo-lede">
          A specialised IGCSE Mathematics practice built around experience, reach,
          and results — for learners aiming for top grades worldwide.
        </p>

        <div className="xm-stat-grid" aria-label="Key demographics">
          {MARKETING_STATS.map((stat, index) => (
            <StatCard
              key={stat.label}
              {...stat}
              active={inView}
              delay={index * 90}
            />
          ))}
        </div>

        <div className="xm-offer-block">
          <div className="xm-offer-intro">
            <p className="xm-kicker">What you get</p>
            <h3 className="xm-offer-heading xm-display">Everything built into the X-Maths experience</h3>
          </div>

          <ul className={`xm-offer-grid ${inView ? "is-visible" : ""}`} aria-label="What you get">
            {OFFERINGS.map((item, index) => (
              <li
                className="xm-offer-item"
                key={item.title}
                style={{ transitionDelay: `${180 + index * 70}ms` }}
              >
                <span className="xm-offer-icon">{OFFER_ICONS[item.icon]}</span>
                <div className="xm-offer-copy">
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

const schoolContext = require.context("./assets/schools", false, /\.(png|jpe?g|svg|webp)$/);

function schoolLabel(fileKey) {
  const base = fileKey.replace(/^\.\//, "").replace(/\.[^.]+$/, "");
  return base
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const SCHOOL_LOGOS = schoolContext
  .keys()
  .sort()
  .map((key) => ({
    src: schoolContext(key),
    name: schoolLabel(key),
  }));

function schoolLoop(logos) {
  if (!logos.length) return [];
  const copies = Math.max(1, Math.ceil(8 / logos.length));
  return Array.from({ length: copies }, () => logos).flat();
}

export function SchoolMarquee() {
  const sequence = schoolLoop(SCHOOL_LOGOS);
  if (!sequence.length) return null;

  const row = (hidden) => (
    <div className="xm-marquee-group" aria-hidden={hidden || undefined}>
      {sequence.map((school, index) => (
        <img
          key={`${hidden ? "b" : "a"}-${school.name}-${index}`}
          src={school.src}
          alt={hidden ? "" : school.name}
          className="xm-school-logo"
        />
      ))}
    </div>
  );

  return (
    <section className="xm-schools" aria-label="Schools">
      <div className="xm-schools-head">
        <p className="xm-kicker">Where we teach</p>
        <h2 className="xm-section-title xm-display">Schools we work with</h2>
      </div>
      <div className="xm-marquee">
        <div className="xm-marquee-track">
          {row(false)}
          {row(true)}
        </div>
      </div>
    </section>
  );
}

const galleryContext = require.context("./assets/helmy-gallery", false, /\.(jpeg|mp4)$/);

const GALLERY_FILES = galleryContext
  .keys()
  .sort()
  .map((key) => ({
    src: galleryContext(key),
    type: key.endsWith(".mp4") ? "video" : "image",
  }));

const GALLERY_PHOTOS = GALLERY_FILES.filter((item) => item.type === "image");
const GALLERY_VIDEOS = GALLERY_FILES.filter((item) => item.type === "video");

export function PhotoAlbum({
  kicker = "Life at X-Maths",
  title = "Learning that feels like a community",
  copy = "From classroom energy to shared celebrations — X-Maths is built around students, not just content.",
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const thumbsRef = useRef(null);
  const count = GALLERY_PHOTOS.length;

  const goTo = (next) => {
    if (!count) return;
    setIndex((next + count) % count);
  };

  useEffect(() => {
    if (paused || count < 2) return undefined;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (motionQuery.matches) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [paused, count]);

  useEffect(() => {
    const rail = thumbsRef.current;
    const activeThumb = rail?.children[index];
    if (!rail || !activeThumb) return;
    const target =
      activeThumb.offsetLeft - rail.clientWidth / 2 + activeThumb.clientWidth / 2;
    rail.scrollTo({ left: target, behavior: "smooth" });
  }, [index]);

  if (!count && !GALLERY_VIDEOS.length) return null;

  return (
    <>
      {count ? (
        <section className="xm-section xm-section-alt">
          <div className="xm-wrap">
            <p className="xm-kicker">{kicker}</p>
            <h2 className="xm-section-title xm-display">{title}</h2>
            <p className="xm-section-copy" style={{ marginBottom: "1.5rem" }}>
              {copy}
            </p>

            <div
              className="xm-slider"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
            >
              <div className="xm-slider-window">
                <div
                  className="xm-slider-track"
                  style={{ transform: `translateX(-${index * 100}%)` }}
                >
                  {GALLERY_PHOTOS.map((photo, photoIndex) => (
                    <figure className="xm-slide" key={photo.src}>
                      <img className="xm-slide-blur" src={photo.src} alt="" aria-hidden="true" />
                      <img
                        className="xm-slide-photo"
                        src={photo.src}
                        alt={`X-Maths classroom moment ${photoIndex + 1}`}
                      />
                    </figure>
                  ))}
                </div>
                <span className="xm-slider-count">
                  {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
                </span>
                <button
                  type="button"
                  className="xm-slider-arrow xm-slider-prev"
                  onClick={() => goTo(index - 1)}
                  aria-label="Previous slide"
                >
                  <span aria-hidden="true">‹</span>
                </button>
                <button
                  type="button"
                  className="xm-slider-arrow xm-slider-next"
                  onClick={() => goTo(index + 1)}
                  aria-label="Next slide"
                >
                  <span aria-hidden="true">›</span>
                </button>
                <span className="xm-slider-progress" aria-hidden="true">
                  <span key={index} className={paused ? "is-paused" : ""} />
                </span>
              </div>

              <div className="xm-slider-thumbs" ref={thumbsRef}>
                {GALLERY_PHOTOS.map((photo, photoIndex) => (
                  <button
                    key={photo.src}
                    type="button"
                    className={`xm-slider-thumb ${photoIndex === index ? "is-active" : ""}`}
                    onClick={() => goTo(photoIndex)}
                    aria-label={`Show slide ${photoIndex + 1}`}
                  >
                    <img src={photo.src} alt="" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {GALLERY_VIDEOS.length ? (
        <section className="xm-section xm-reels">
          <div className="xm-wrap xm-reels-layout">
            <div className="xm-reels-copy">
              <p className="xm-kicker">On film</p>
              <h2 className="xm-section-title xm-display">The classroom, in motion</h2>
              <p className="xm-section-copy">
                Short clips from lessons — stay a moment with the class.
              </p>
            </div>
            <div className="xm-reel-stage">
              {GALLERY_VIDEOS.map((video) => (
                <div className="xm-reel" key={video.src}>
                  <video src={video.src} controls playsInline preload="metadata" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
